import {Component, Vue} from 'vue-property-decorator';
import {mapState} from 'vuex';
import PlanEditResultConfigComponentsEnum from '@/enums/PlanEditResultConfigComponentsEnum';
import {PlanEditResultConfigItem} from '@/models/PlanEditResultConfigModel';
import {WarehouseDescription} from '@/models/PlanEditModel';
import PlanEditResultModel from '@/models/PlanEditResultModel';
import PlanEditResultConfigTabTypesEnum from '@/enums/PlanEditResultConfigTabTypesEnum';
import PlanEditResultShiftEnum from '@/enums/PlanEditResultShiftEnum';

@Component({
    computed: {
        ...mapState('planEditResult', ['isEditing']),
        ...mapState('planEditResult', ['result', 'equipmentTypesFromCatalogue']),
        ...mapState('planEdit', ['warehouseVeneerDescriptions', 'warehousePlywoodDescriptions']),
    },
})
export default class PlanEditResultMixin extends Vue {
    public isEditing!: boolean;
    public result!: PlanEditResultModel;
    public warehouseVeneerDescriptions!: WarehouseDescription[];
    public warehousePlywoodDescriptions!: WarehouseDescription[];
    public equipmentTypesFromCatalogue!: WarehouseDescription[];

    get byDefault() {
        return {id: -1, description: this.$t('Common.FromTab')};
    }

    get componentsList() {
        return [
            {
                key: PlanEditResultConfigComponentsEnum.Tabs,
                component: () =>
                    this.isEditing
                        ? import('@/views/journals/planEdit/planEditTabs/result/edit/PlanEditResultEditTabs.vue')
                        : import('@/views/journals/planEdit/planEditTabs/result/PlanEditResultTabs.vue'),
                title: this.$t('Documents.Plan.Tabs'),
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResSummaryOrderBalance,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResSummaryOrderBalance.vue'),
                title: this.$t('Documents.PlanRes.OrderBalance'),
                api: this.$api.journals.planResSummaryOrderBalance,
                requestParams: [],
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResSummaryPlywoodBalance,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResSummaryPlywoodBalance.vue'),
                title: this.$t('Documents.PlanRes.PlywoodBalance'),
                api: this.$api.journals.planResSummaryPlywoodBalance,
                requestParams: [],
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResSummaryWarehouseVeneer,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResSummaryWarehouseVeneer.vue'),
                title: this.$t('Documents.PlanRes.VeneerBalance'),
                api: this.$api.journals.planResSummaryWarehouseVeneer,
                requestParams: [],
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResSummaryIndicator,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResSummaryIndicator.vue'),
                title: this.$t('Common.Summary'),
                requestParams: [],
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResSummaryList,
                component: () => import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResSummaryList.vue'),
                title: this.$t('Common.TargetSummary'),
                requestParams: [],
            },
            {
                key: PlanEditResultConfigComponentsEnum.EquipmentType,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/equipmentType/EquipmentType.vue'),
                title: this.$t('Documents.PlanRes.EquipmentTypePlanByShift'),
                requestParams: [
                    {
                        key: 'shiftIndex',
                        getDescriptions: this.getShiftsDescriptions,
                        title: this.$t('Catalogues.ShiftTml.Shift'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Shift,
                    },
                    {
                        key: 'idEquipmentType',
                        getDescriptions: this.getEquipmentTypesDescriptions,
                        title: this.$t('Catalogues.Equipments.EquipmentType.NameSingle'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Equipment,
                    },
                ],
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResByShiftWarehouseVeneer,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResByShiftWarehouseVeneer.vue'),
                title: this.$t('Documents.PlanRes.VeneerBalanceByShift'),
                requestParams: [
                    {
                        key: 'shiftIndex',
                        getDescriptions: this.getShiftsDescriptions,
                        title: this.$t('Catalogues.ShiftTml.Shift'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Shift,
                    },
                    {
                        key: 'idWarehouseVeneer',
                        getDescriptions: this.getWarehouseVeneerDescriptions,
                        title: this.$t('Catalogues.Warehouses.WarehouseVeneer.NameSingle'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.WarehouseVeneer,
                    },
                ],
                isCompressed: false,
                // api: this.$api.journals.planResByShiftWarehouseVeneer
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResByShiftOrderBalance,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResByShiftOrderBalance.vue'),
                title: this.$t('Documents.PlanRes.OrderBalanceByShift'),
                customTitle: this.$t('Documents.PlanRes.OrderBalance'),
                requestParams: [
                    {
                        key: 'shiftIndex',
                        getDescriptions: this.getShiftsDescriptions,
                        title: this.$t('Catalogues.ShiftTml.Shift'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Shift,
                    },
                ],
                api: this.$api.journals.planResByShiftOrderBalance,
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResByShiftPlywoodBalance,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResByShiftPlywoodBalance.vue'),
                title: this.$t('Documents.PlanRes.PlywoodBalanceByShift'),
                requestParams: [
                    {
                        key: 'shiftIndex',
                        getDescriptions: this.getShiftsDescriptions,
                        title: this.$t('Catalogues.ShiftTml.Shift'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Shift,
                    },
                ],
                api: this.$api.journals.planResByShiftPlywoodBalance,
                isCompressed: false,
            },
            {
                key: PlanEditResultConfigComponentsEnum.PlanResByShiftRecommendation,
                component: () =>
                    import('@/views/journals/planEdit/planEditTabs/result/tables/PlanResByShiftRecommendation.vue'),
                title: this.$t('Documents.PlanRes.RecommendationByShift'),
                requestParams: [
                    {
                        key: 'shiftIndex',
                        getDescriptions: this.getShiftsDescriptions,
                        title: this.$t('Catalogues.ShiftTml.Shift'),
                        dependencieTab: PlanEditResultConfigTabTypesEnum.Shift,
                    },
                ],
                api: this.$api.journals.planResByShiftRecommendation,
                isCompressed: false,
            },
        ];
    }

    get descriptions() {
        return this.componentsList.map(item => ({
            key: item.key,
            description: item.title,
        }));
    }

    getComponentData(key: PlanEditResultConfigComponentsEnum) {
        let value = this.componentsList.find(item => item.key === key);
        return value ? value : null;
    }

    getComponent(key: PlanEditResultConfigComponentsEnum) {
        let value = this.componentsList.find(item => item.key === key);
        return value ? value.component : null;
    }

    getComponentTitle(key: PlanEditResultConfigComponentsEnum) {
        let value = this.componentsList.find(item => item.key === key);
        return value ? value.title : null;
    }

    getComponentRequestParams(key: PlanEditResultConfigComponentsEnum): any[] | null | undefined {
        let value = this.componentsList.find(item => item.key === key);
        return value ? value.requestParams : null;
    }

    getComponentApi(key: PlanEditResultConfigComponentsEnum) {
        let value = this.componentsList.find(item => item.key === key);
        return value ? value.api : null;
    }

    createItem(key: PlanEditResultConfigComponentsEnum, depth: number, activeTabs: any[]): PlanEditResultConfigItem {
        if (key === PlanEditResultConfigComponentsEnum.Tabs)
            return {
                componentKey: key,
                props: {
                    depth,
                    tabs: [],
                },
            };
        else
            return {
                componentKey: key,
                props: {
                    depth,
                },
                requestParams: this.getComponentRequestParams(key)?.map(param => {
                    let value = -2;
                    if (activeTabs.includes(param.dependencieTab)) value = -1;
                    return {
                        key: param.key,
                        value,
                    };
                }),
                idGridConfigItem: null,
                isCompressed: this.getComponentData(key)?.isCompressed,
            };
    }

    getShiftsDescriptions(disabledDefault?: boolean) {
        const shifts = [
            {
                id: PlanEditResultShiftEnum.First,
                description: this.$t('Documents.PlanRes.FirstShift'),
            },
            {
                id: PlanEditResultShiftEnum.Last,
                description: this.$t('Documents.PlanRes.LastShift'),
            },
        ];
        return [Object.assign({}, this.byDefault, {disabled: disabledDefault}), ...shifts];
    }

    getEquipmentTypesDescriptions(disabledDefault?: boolean) {
        const ets = this.equipmentTypesFromCatalogue.map(equipment => ({
            id: equipment.Id,
            description: equipment.Description,
        }));
        return [Object.assign({}, this.byDefault, {disabled: disabledDefault}), ...ets];
    }

    getWarehouseVeneerDescriptions(disabledDefault?: boolean) {
        const wvs = this.warehouseVeneerDescriptions.map(wv => ({
            id: wv.Id,
            description: wv.Description,
        }));
        return [
            Object.assign({}, this.byDefault, {disabled: disabledDefault}),
            {
                id: null,
                description: this.$t('Catalogues.Warehouses.Warehouse.AllWarehouses'),
            },
            ...wvs,
        ];
    }

    getWarehousePlywoodDescriptions(disabledDefault?: boolean) {
        const wps = this.warehousePlywoodDescriptions.map(wp => ({
            id: wp.Id,
            description: wp.Description,
        }));
        return [
            Object.assign({}, this.byDefault, {disabled: disabledDefault}),
            {
                id: null,
                description: this.$t('Catalogues.Warehouses.Warehouse.AllWarehouses'),
            },
            ...wps,
        ];
    }
}
